/**
 * Dialog „Termin anlegen“ bzw. „Termin bearbeiten“ – eine manuelle Frist (ADR 010).
 *
 * Oben der Umschalter „Persönlich | Projekt“:
 * - **Persönlich** – ein Termin ohne Bezug, der dem Anlegenden gehört. Verantwortlich ist man
 *   selbst (nicht änderbar), einen Bezug gibt es nicht.
 * - **Projekt** – ein Projekttermin am Projekt, an einem Antrag oder an einem Nachweis. Das
 *   Projekt ist Pflicht, Antrag und Nachweis verfeinern ihn; Verantwortlich ist frei wählbar.
 *
 * Derselbe Dialog an allen Stellen. Den Umschalter gibt es nur auf der Startseite, dort startet
 * er persönlich. An Projekt, Antrag und Nachweis legt man immer einen Termin dieses Bezugs an.
 *
 * Der Bezug eines Projekttermins steht oben als Pfad („Projekt › Antrag › Nachweis“). Was der
 * Ort vorgibt, steht fest – am Projekt das Projekt, am Antrag dazu der Antrag, am Nachweis alle
 * drei. Die nächste Ebene wählt man im Pfad selbst („Ganzes Projekt ▾“, „Ganzer Antrag ▾“). Beim
 * Bearbeiten und Lesen ist der Pfad fest.
 *
 * Ein bestehender Termin öffnet sich zum **Lesen**: Erledigen (primär), Bearbeiten, Schließen und
 * abgesetzt Löschen. „Bearbeiten“ macht die Felder änderbar; „Abbrechen“ und „Speichern“ führen
 * zurück zum Lesen, nach dem Speichern mit den neuen Werten. Ohne Schreibrecht bleibt nur
 * „Schließen“. Beim Bearbeiten steht die Art fest – ein persönlicher Termin wird nicht
 * nachträglich zum Projekttermin.
 *
 * Projekte, Anträge, Nachweise und Benutzer lädt der Dialog beim Öffnen selbst: Er wird
 * von Seiten geöffnet, die diese Listen sonst nicht brauchen.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router";
import { addDays, format } from "date-fns";
import DialogTemplate, { DialogSection } from "../../layout/DialogTemplate";
import Button from "../../common/inputs/Button";
import SelectableBreadcrumb, {
  BreadcrumbOption,
  BreadcrumbSegment,
} from "../../common/inputs/SelectableBreadcrumb";
import Dropdown from "../../common/inputs/Dropdown";
import InputField from "../../common/inputs/InputField";
import InputFieldDate from "../../common/inputs/InputFieldDate";
import InputFieldMultiline from "../../common/inputs/InputFieldMultiline";
import ToggleSwitch from "../../common/inputs/ToggleSwitch";
import { permitsApi } from "../../../services/api/permits";
import { projectsApi } from "../../../services/api/projects";
import { OrgUser, usersApi } from "../../../services/api/users";
import { useAuth } from "../../../auth/AuthProvider";
import {
  DeadlineItem,
  DeadlineSubmission,
  PermitProcedure,
  ProjectEvidence,
} from "../../../types";
import { deadlinesApi } from "../../../services/api/deadlines";
import {
  isDone,
  isPersonal,
  referenceLinkLabel,
  referencePath,
} from "../../../utils/deadlines";
import { formatDate } from "../../../utils/formatters";
import { colors } from "../../../styles/theme";

type Kind = DeadlineSubmission["kind"];

interface ManualDeadlineDialogProps {
  open: boolean;
  /** Projekt, an dem geöffnet wurde – startet als Projekttermin mit diesem Projekt */
  projectId?: string;
  /**
   * Vorausgewähltes Projekt ohne festen Kontext (Startseite mit ausgewähltem Projekt): startet
   * als Projekttermin mit diesem Projekt, Projekt und „Persönlich“ bleiben wählbar
   */
  presetProjectId?: string;
  /** Vorbelegter Bezug beim Anlegen – der Antrag bzw. Nachweis, an dem geöffnet wurde */
  presetPermitProcedureId?: string | null;
  presetEvidenceId?: string | null;
  /** Ein bestehender Termin statt Anlegen – öffnet sich zum Lesen */
  deadline?: DeadlineItem | null;
  /** Ob man den bestehenden Termin pflegen darf; sonst gibt es nur „Schließen“ */
  canEdit?: boolean;
  /** Link zur Seite des Bezugs – entfällt, wenn man schon dort steht */
  showReferenceLink?: boolean;
  onClose: () => void;
  /** Speichert; `false`, wenn der Server abgelehnt hat (Dialog bleibt offen) */
  onSubmit: (submission: DeadlineSubmission) => Promise<boolean>;
  /** Erledigen bzw. wieder öffnen – der Aufrufer schließt den Dialog und bietet „Rückgängig“ */
  onSetDone?: (done: boolean) => void;
  /** Löschen – der Aufrufer fragt nach */
  onDelete?: () => void;
}

/** Die Felder des Formulars – beim „Abbrechen“ im Bearbeiten wieder hergestellt */
interface FormValues {
  projectId: string;
  title: string;
  dueDate: string;
  responsibleId: string;
  note: string;
  problem: boolean;
  procedureId: string;
  evidenceId: string;
}

/** Leerer Wert der Auswahl „Antrag“ und „Nachweis“ – der Termin hängt dann eine Ebene höher */
const NONE = "";

/** Vorbelegung „Fällig am“: heute in zwei Wochen */
const DEFAULT_DUE_IN_DAYS = 14;

const procedureLabel = (procedure: PermitProcedure): string =>
  procedure.bezeichnung || procedure.procedure_type?.name || "Ohne Bezeichnung";

const ManualDeadlineDialog: React.FC<ManualDeadlineDialogProps> = ({
  open,
  projectId: contextProjectId,
  presetProjectId,
  presetPermitProcedureId,
  presetEvidenceId,
  deadline,
  canEdit = false,
  showReferenceLink = false,
  onClose,
  onSubmit,
  onSetDone,
  onDelete,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kind, setKind] = useState<Kind>("personal");
  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [responsibleId, setResponsibleId] = useState("");
  const [note, setNote] = useState("");
  const [problem, setProblem] = useState(false);
  const [procedureId, setProcedureId] = useState(NONE);
  const [evidenceId, setEvidenceId] = useState(NONE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState<"view" | "edit">("edit");
  /** Stand beim Wechsel ins Bearbeiten – „Abbrechen“ kehrt zu ihm zurück */
  const [snapshot, setSnapshot] = useState<FormValues | null>(null);

  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [procedures, setProcedures] = useState<PermitProcedure[]>([]);
  const [evidences, setEvidences] = useState<ProjectEvidence[]>([]);

  const editing = !!deadline;
  const viewing = editing && mode === "view";
  const personal = kind === "personal";

  // Beim Öffnen vorbelegen: aus der Frist beim Bearbeiten, sonst aus dem Ort des Aufrufs.
  useEffect(() => {
    if (!open) return;
    setKind(
      deadline
        ? isPersonal(deadline)
          ? "personal"
          : "project"
        : contextProjectId || presetProjectId
          ? "project"
          : "personal",
    );
    setProjectId(
      deadline?.project_id ?? contextProjectId ?? presetProjectId ?? "",
    );
    setTitle(deadline?.bezeichnung ?? "");
    setDueDate(
      deadline?.zieltermin ??
        format(addDays(new Date(), DEFAULT_DUE_IN_DAYS), "yyyy-MM-dd"),
    );
    setResponsibleId(
      deadline
        ? (deadline.verantwortlich?.external_id ?? "")
        : (user?.id ?? ""),
    );
    setNote(deadline?.note ?? "");
    setProblem(!!deadline?.problem);
    setProcedureId(
      (deadline ? deadline.permit_procedure_id : presetPermitProcedureId) ??
        NONE,
    );
    setEvidenceId((deadline ? deadline.evidence_id : presetEvidenceId) ?? NONE);
    setErrors({});
    setMode(deadline ? "view" : "edit");
    setSnapshot(null);
  }, [
    open,
    deadline,
    contextProjectId,
    presetProjectId,
    presetPermitProcedureId,
    presetEvidenceId,
    user?.id,
  ]);

  useEffect(() => {
    if (!open) return;
    usersApi
      .getOrgUsers()
      .then(setOrgUsers)
      .catch(() => setOrgUsers([]));
    projectsApi
      .getProjects()
      .then(setProjects)
      .catch(() => setProjects([]));
  }, [open]);

  useEffect(() => {
    if (!open || !projectId || personal) {
      setProcedures([]);
      setEvidences([]);
      return;
    }
    permitsApi
      .getPermitProcedures(projectId)
      .then(setProcedures)
      .catch(() => setProcedures([]));
    permitsApi
      .getProjectEvidences(projectId)
      .then(setEvidences)
      .catch(() => setEvidences([]));
  }, [open, projectId, personal]);

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name })),
    [projects],
  );
  const userOptions = useMemo(
    () => orgUsers.map((u) => ({ value: u.user_id, label: u.name || u.email })),
    [orgUsers],
  );
  const procedureOptions = useMemo(
    () => [
      { value: NONE, label: "Ganzes Projekt" },
      ...procedures.map((p) => ({ value: p.id, label: procedureLabel(p) })),
    ],
    [procedures],
  );
  // Nur Nachweise des gewählten Antrags; archivierte bekommen keine neuen Termine.
  const evidenceOptions = useMemo(
    () => [
      { value: NONE, label: "Ganzer Antrag" },
      ...evidences
        .filter(
          (e) =>
            e.permit_procedure_id === procedureId &&
            (!e.archiviert || e.id === evidenceId),
        )
        .map((e) => ({
          value: e.id,
          label: e.bezeichnung || e.evidence_type.name,
        })),
    ],
    [evidences, procedureId, evidenceId],
  );

  const validate = (): boolean => {
    const found: Record<string, string> = {};
    if (!title.trim()) found.title = "Pflichtfeld";
    if (!dueDate) found.dueDate = "Pflichtfeld";
    if (!personal && !responsibleId) found.responsibleId = "Pflichtfeld";
    if (!personal && !projectId) found.projectId = "Bitte ein Projekt wählen";
    // Ein Problem ist in der Notiz beschrieben – ohne sie wüsste niemand, was hakt.
    if (problem && !note.trim()) found.note = "Beschreibt das Problem";
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setRunning(true);
    try {
      const base = {
        title: title.trim(),
        due_date: dueDate,
        note: note.trim() || null,
        problem,
      };
      const ok = await onSubmit(
        personal
          ? { kind: "personal", data: base }
          : {
              kind: "project",
              projectId,
              data: {
                ...base,
                responsible_user_id: responsibleId,
                permit_procedure_id: procedureId || null,
                evidence_id: evidenceId || null,
              },
            },
      );
      if (!ok) return;
      // Ein bestehender Termin kehrt zum Lesen zurück – die Felder halten die gespeicherten Werte.
      if (editing) {
        setSnapshot(null);
        setMode("view");
      } else {
        onClose();
      }
    } finally {
      setRunning(false);
    }
  };

  const startEditing = () => {
    setSnapshot({
      projectId,
      title,
      dueDate,
      responsibleId,
      note,
      problem,
      procedureId,
      evidenceId,
    });
    setMode("edit");
  };

  const cancelEditing = () => {
    if (snapshot) {
      setProjectId(snapshot.projectId);
      setTitle(snapshot.title);
      setDueDate(snapshot.dueDate);
      setResponsibleId(snapshot.responsibleId);
      setNote(snapshot.note);
      setProblem(snapshot.problem);
      setProcedureId(snapshot.procedureId);
      setEvidenceId(snapshot.evidenceId);
    }
    setSnapshot(null);
    setErrors({});
    setMode("view");
  };

  // Persönlich: man selbst, nicht änderbar. Steht man nicht in der Benutzerliste, zeigt das
  // Feld trotzdem den eigenen Namen.
  const ownOption = {
    value: user?.id ?? "",
    label: user?.display_name || user?.email || "Ich",
  };
  const responsibleOptions =
    personal && !userOptions.some((o) => o.value === ownOption.value)
      ? [ownOption, ...userOptions]
      : userOptions;

  const labelOf = (
    options: { value: string; label: string }[],
    value: string,
    fallback?: string | null,
  ): string => options.find((o) => o.value === value)?.label || fallback || "–";

  const done = !!deadline && isDone(deadline);
  const actions = viewing ? (
    <>
      {canEdit && onDelete && (
        // Abgesetzt links: `Button` steckt in einem eigenen Wrapper, `mr` am Knopf selbst
        // verschiebt ihn nicht.
        <Box sx={{ mr: "auto" }}>
          <Button variant="danger" onClick={onDelete}>
            Löschen
          </Button>
        </Box>
      )}
      <Button variant="secondary" onClick={onClose}>
        Schließen
      </Button>
      {canEdit && (
        <Button variant="secondary" onClick={startEditing}>
          Bearbeiten
        </Button>
      )}
      {canEdit && onSetDone && (
        <Button variant="primary" onClick={() => onSetDone(!done)}>
          {done ? "Wieder öffnen" : "Erledigen"}
        </Button>
      )}
    </>
  ) : (
    <>
      <Button
        variant="secondary"
        onClick={editing ? cancelEditing : onClose}
        disabled={running}
      >
        Abbrechen
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={running}>
        {editing ? "Speichern" : "Termin anlegen"}
      </Button>
    </>
  );

  const referenceLink = showReferenceLink && projectId && (
    <Link
      component="button"
      type="button"
      variant="body2"
      underline="hover"
      onClick={() => {
        onClose();
        navigate(
          referencePath({
            projectId,
            permitProcedureId: procedureId || null,
            evidenceId: evidenceId || null,
          }),
        );
      }}
    >
      {referenceLinkLabel({
        permitProcedureId: procedureId || null,
        evidenceId: evidenceId || null,
      })}
    </Link>
  );

  // Lesen: die Bezeichnung als Überschrift, darunter die Angaben zweispaltig, die Notiz als
  // Fließtext über die ganze Breite – kompakt statt der Abschnitte des Formulars.
  const facts: { label: string; value: React.ReactNode }[] = [
    { label: "Fällig am", value: dueDate ? formatDate(dueDate) : "–" },
    {
      label: "Verantwortlich",
      value: labelOf(
        responsibleOptions,
        personal ? ownOption.value : responsibleId,
        deadline?.verantwortlich?.display_name ||
          deadline?.verantwortlich?.username,
      ),
    },
    // Der Bezug eines Projekttermins steht oben im Pfad.
    ...(personal ? [{ label: "Bezug", value: "Persönlich" }] : []),
    ...(done && deadline?.done_at
      ? [{ label: "Erledigt am", value: formatDate(deadline.done_at) }]
      : []),
  ];

  /**
   * Der Bezug als Pfad. Wählbar ist ein Glied nur beim Anlegen und nur, wenn der Ort es nicht
   * vorgibt; ein festes Glied ohne Wahl („kein Antrag“) entfällt. Solange die Listen laden, steht
   * „…“ statt eines Namens.
   */
  const creating = !editing;
  const segment = (
    key: string,
    options: BreadcrumbOption[],
    value: string,
    fallback: string | null | undefined,
    onChange: ((value: string) => void) | false,
  ): BreadcrumbSegment => ({
    key,
    label: options.find((o) => o.value === value)?.label || fallback || "…",
    ...(onChange && { selection: { options, value, onChange } }),
  });
  const procedureSelectable = creating && !presetPermitProcedureId;
  const evidenceSelectable = creating && !presetEvidenceId;
  const segments: BreadcrumbSegment[] = [
    projectId
      ? segment(
          "project",
          projectOptions,
          projectId,
          deadline?.project_name,
          creating &&
            !contextProjectId &&
            ((value) => {
              setProjectId(value);
              setProcedureId(NONE);
              setEvidenceId(NONE);
              setErrors((prev) => ({ ...prev, projectId: "" }));
            }),
        )
      : {
          key: "project",
          label: "Projekt wählen",
          placeholder: true,
          selection: {
            options: projectOptions,
            value: "",
            onChange: (value) => {
              setProjectId(value);
              setErrors((prev) => ({ ...prev, projectId: "" }));
            },
          },
        },
    ...(projectId && (procedureId || procedureSelectable)
      ? [
          segment(
            "procedure",
            procedureOptions,
            procedureId,
            evidenceId ? null : deadline?.reference_label,
            procedureSelectable &&
              ((value) => {
                setProcedureId(value);
                setEvidenceId(NONE);
              }),
          ),
        ]
      : []),
    ...(procedureId && (evidenceId || evidenceSelectable)
      ? [
          segment(
            "evidence",
            evidenceOptions,
            evidenceId,
            deadline?.reference_label,
            evidenceSelectable &&
              ((value) => {
                setEvidenceId(value);
                setErrors((prev) => ({ ...prev, evidenceId: "" }));
              }),
          ),
        ]
      : []),
  ];
  const breadcrumb = (
    <SelectableBreadcrumb
      segments={segments}
      readOnly={viewing}
      ariaLabel="Bezug"
      error={viewing ? undefined : errors.projectId || errors.evidenceId}
    />
  );

  const viewContent = (
    <Box sx={{ pt: 1 }}>
      {!personal && <Box sx={{ mb: 1.5 }}>{breadcrumb}</Box>}
      <Typography variant="subtitle1" sx={{ mb: 2, wordBreak: "break-word" }}>
        {title}
      </Typography>
      <Box
        component="dl"
        sx={{
          display: "grid",
          // Zweispaltig, je Angabe das Label über dem Wert; auf schmalen Bildschirmen einspaltig.
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: 3,
          rowGap: 2,
          m: 0,
          "& dt": { color: colors.text.secondary, mb: 0.25 },
          "& dd": { m: 0, color: colors.text.primary },
        }}
      >
        {facts.map((fact) => (
          <Box key={fact.label} sx={{ minWidth: 0 }}>
            <Typography component="dt" variant="body2">
              {fact.label}
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              sx={{ wordBreak: "break-word" }}
            >
              {fact.value}
            </Typography>
          </Box>
        ))}
      </Box>
      {referenceLink && <Box sx={{ mt: 1.5 }}>{referenceLink}</Box>}
      {note && (
        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${colors.border.divider}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: problem ? colors.status.error.main : colors.text.secondary,
              mb: 0.5,
            }}
          >
            {problem ? "Problem" : "Notiz"}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {note}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <DialogTemplate
      open={open}
      onClose={onClose}
      title={
        viewing ? "Termin" : editing ? "Termin bearbeiten" : "Termin anlegen"
      }
      maxWidth="sm"
      actions={actions}
    >
      {viewing ? (
        viewContent
      ) : (
        <Box sx={{ pt: 1 }}>
          {!editing && !contextProjectId && (
            <ToggleButtonGroup
              value={kind}
              exclusive
              fullWidth
              size="small"
              onChange={(_, next: Kind | null) => {
                if (!next) return;
                setKind(next);
                setErrors({});
                if (next === "personal") setResponsibleId(user?.id ?? "");
              }}
              sx={{
                mb: 3,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  color: colors.text.secondary,
                  "&.Mui-selected": {
                    backgroundColor: colors.brand.primary,
                    color: colors.background.paper,
                    "&:hover": { backgroundColor: colors.brand.primaryDark },
                  },
                },
              }}
            >
              <ToggleButton value="personal">Persönlich</ToggleButton>
              <ToggleButton value="project">Projekt</ToggleButton>
            </ToggleButtonGroup>
          )}

          {!personal && <Box sx={{ mb: 3 }}>{breadcrumb}</Box>}

          <DialogSection title="Termin" first>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <InputField
                label="Bezeichnung"
                value={title}
                onChange={setTitle}
                required
                error={errors.title}
                size="small"
                autoFocus
              />
              <InputFieldDate
                label="Fällig am"
                value={dueDate}
                onChange={(value) => setDueDate(value ?? "")}
                variant="floating"
                required
                error={errors.dueDate}
              />
              <Dropdown
                label="Verantwortlich"
                value={personal ? ownOption.value : responsibleId}
                options={responsibleOptions}
                onChange={setResponsibleId}
                variant="floating"
                size="small"
                searchable
                required
                disabled={personal}
                error={!!errors.responsibleId}
                helperText={errors.responsibleId}
              />
            </Box>
          </DialogSection>

          <DialogSection title="Notiz">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Ein Problem ersetzt in den Übersichten das Notiz-Icon durch ein Ausrufezeichen. */}
              <ToggleSwitch
                value={problem ? "problem" : "note"}
                options={[
                  { value: "note", label: "Notiz" },
                  { value: "problem", label: "Problem" },
                ]}
                onChange={(value) => {
                  setProblem(value === "problem");
                  setErrors((prev) => ({ ...prev, note: "" }));
                }}
              />
              <InputFieldMultiline
                label={problem ? "Was hakt?" : "Notiz"}
                value={note}
                onChange={setNote}
                required={problem}
                error={errors.note}
                minRows={3}
              />
            </Box>
          </DialogSection>
        </Box>
      )}
    </DialogTemplate>
  );
};

export default ManualDeadlineDialog;
